import { Component, OnInit, Input, OnDestroy, ViewChildren, ViewChild, ElementRef } from '@angular/core';
import { extract } from '../core/i18n.service';
import { Observable, Subscription } from 'rxjs';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { ConfirmationService } from '../shared/dialog/confirmation.service';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDividerModule } from '@angular/material/divider';
import { FormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { CommonModule } from '@angular/common';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { ShellStateService } from '../shell-state.service';
import { AuthenticationService } from '../core/authentication/authentication.service';
import { ApiService } from '../core/api/api.service';
import { ShareLinkService } from './share-link.service';
import { MessageBoxService } from '../shared/dialog/messagebox.service';
import { DialogQRCodeComponent } from '../dialog-qrcode/dialog-qrcode.component';
import { QRCodesService } from './qrcodes.service';
import { LoaderService } from '../shared/loader/loader.service';
import { MatButtonModule } from '@angular/material/button';
import { MODULE_TYPE } from '../../../../commondata/messages/msg-constants';
import { Logger } from '../core/logger.service';
import { StatDto } from '../core/stats/stat-dto';
import { STATS } from '../../../../commondata/stats/stats-constants';
import { MatomoTracker } from 'ngx-matomo-client';

export class UserItem {
    caller: any;
    isOwner: boolean = false;
    picture: string = '';
    name: string = '';
    email: string = '';
    ownerNotified: string | null = '';
    private _role: string = '';
    public get role(): string {
        return this._role;
    }
    public set role(value: string) {
        this._role = value;
        switch (value) {
            case 'owner':
                this.isOwner = true;
                this._internalRole = 'owner';
                break;
            case 'writer':
                this._internalRole = 'edit';
                break;
            case 'reader':
            case 'commenter':
                this._internalRole = 'view';
                break;
        }
        if (!this.caller.loaderService.isLoading()) {
            this.caller.askSaveData();
        }
    }
    private _internalRole: string = '';
    public get internalRole(): string {
        return this._internalRole;
    }
    public set internalRole(value: string) {
        this._internalRole = value;
        switch (value) {
            case 'remove':
                this.caller.deleteUser(this);
                break;
            case 'view':
                this._role = 'reader';
                break;
            case 'edit':
                this._role = 'writer';
                break;
        }
        if (!this.caller.loaderService.isLoading()) {
            this.caller.askSaveData();
        }
    }
}

const logger: Logger = new Logger('ShareDocComponent');

@Component({
    selector: 'app-share-doc',
    imports: [
        CommonModule,
        TranslateModule,
        MatInputModule,
        MatIconModule,
        MatDialogModule,
        MatTooltipModule,
        MatDividerModule,
        MatButtonModule,
        FormsModule,
        MatSelectModule,
        MatAutocompleteModule
    ],
    templateUrl: './share-doc.component.html',
    styleUrls: ['./share-doc.component.scss']
})

export class ShareDocComponent implements OnInit, OnDestroy {

    @ViewChild('addUserNameField') private addUserNameField: ElementRef | undefined;

    @Input() docId: string | undefined;
    @Input() docType: string | undefined;
    @Input() docName: string | undefined;
    @Input() docAuthor: string | undefined;
    @Input() googleUri: string | undefined;
    @Input() googleId: string | undefined;
    @Input() showClassroom = false;
    @Input() showOnlyQrCode = false;

    @ViewChildren('tooltip') tooltips: any;

    private saveDataId: any = null;

    public addUserEmail = '';
    public addUserIsValid = false;
    public loadUsersList = new Array<UserItem>;
    public saveUsersList = new Array<UserItem>;
    public shareEmails = new Array<string>;
    public shareEmailsFreq = new Array<string>;
    public shareEmailsGroup = new Array<string>;

    public urlToShare = '';

    private _publicShareType = 'none';
    private writeShareSubscription: Subscription | undefined;
    public get publicShareType() {
        return this._publicShareType;
    }
    public set publicShareType(value) {
        this._publicShareType = value;
        if (!this.loaderService.isLoading()) {
            this.saveData();
            if (value !== 'none') {
                this.writeSharedStat();
            }
        }
    }

    public shareData: any = null;
    public shareTitle = '';
    public canDo = '';
    public isLogged: boolean;
    public isShared = false;
    public linkCopied: boolean;
    // public isFirstLoggedUser: boolean;
    public isMapPubliclyShared: boolean;
    public isSharing = false;

    public getShareDataSubscription: Subscription | undefined;
    public insertMapSharedSubscription: Subscription | undefined;
    private statShareSubscription: Subscription | undefined;

    public isLinkGenerated: boolean;

    public shareType = 'view';

    public viewShareType: any = null;
    public editShareType: any = null;
    public noneShareType: any = null;
    public removeShareType: any = null;

    public addShareTypesOptions: any = [];
    public editShareTypesOptions: any = [];
    public publicShareTypesOptions: any = [];

    // public origShowFacebook = false;
    public data: any = null;
    public isMapMine = false;
    public canShare = false;
    public accessToken = '';

    filteredOptions: Observable<string[]> | null = null;
    suggestedEmails = new Array<string>();

    constructor(
        public loaderService: LoaderService,
        private dialog: MatDialog,
        private qrCodesService: QRCodesService,
        // private fbService: FbService,
        private apiService: ApiService,
        public shellStateService: ShellStateService,
        private shareLinkService: ShareLinkService,
        private dialogRef: MatDialogRef<ShareDocComponent>,
        public authenticationService: AuthenticationService,
        private confirmationService: ConfirmationService,
        private translateService: TranslateService,
        // private modalService: ModalService,
        private messageBoxService: MessageBoxService,
        private _snackBar: MatSnackBar,
        private tracker: MatomoTracker
    ) {
        this.viewShareType = { value: 'view', name: this.translateService.instant('SHARE_LINK_READONLY'), icon: 'visibility' };
        this.editShareType = { value: 'edit', name: this.translateService.instant('SHARE_LINK_EDIT'), icon: 'edit' };
        this.noneShareType = { value: 'none', name: this.translateService.instant('SHARE_LINK_NONE'), icon: 'lock' };
        this.removeShareType = { value: 'remove', name: this.translateService.instant('SHARE_LINK_REMOVE') };

        this.isLogged = this.authenticationService.isAuthenticated();
        this.linkCopied = false;
        this.urlToShare = '';
        this.showOnlyQrCode = false;
        this.isLinkGenerated = false;
        // this.isFirstLoggedUser = false;
        this.isMapPubliclyShared = false;
    }

    async ngOnInit() {
        this.loaderService.startLoading('generic', 'PLEASE_WAIT');
        // Tipi di condivisione ammessi per tipologia di documento
        this.addShareTypesOptions = (this.docType === 'map' ?
            [this.viewShareType, this.editShareType] :
            [this.viewShareType]
        );
        this.editShareTypesOptions = (this.docType === 'map' ?
            [this.viewShareType, this.editShareType, this.removeShareType] :
            [this.viewShareType, this.removeShareType]
        );
        this.publicShareTypesOptions = (this.docType === 'map' ?
            [this.noneShareType, this.viewShareType, this.editShareType] :
            [this.noneShareType, this.viewShareType]
        );
        //
        this.loadData();
        if (this.isLogged) {
            this.generateLink().then(() => {
                if (this.showOnlyQrCode) {
                    this.closeShareMap();
                    this.showQRCodeAndCopyLink();
                }
            });
        } else {
            this.isLinkGenerated = true;
            this.urlToShare = window.location.href;
            this.closeShareMap();
            this.showQRCodeAndCopyLink();
        }
    }

    ngOnDestroy() {
        // this.shellStateService.setEditingState(false);
        if (this.insertMapSharedSubscription) { this.insertMapSharedSubscription.unsubscribe(); }
        if (this.statShareSubscription) { this.statShareSubscription.unsubscribe(); }
        if (this.getShareDataSubscription) { this.getShareDataSubscription.unsubscribe(); }
    }

    loadData() {
        this.loaderService.startLoading('generic', 'PLEASE_WAIT');
        const email = this.authenticationService.getUserEmail();
        const docId = this.docId ? this.docId : '';
        const googleId = this.googleId ? this.googleId : '';
        const unknownUsername = this.translateService.instant('UNKNOWN_USERNAME');
        if (this.getShareDataSubscription) { this.getShareDataSubscription.unsubscribe(); }
        this.getShareDataSubscription = this.apiService.getShareData(email, docId).subscribe({
            next: (data: any) => {
                console.log('docId: ' + this.docId);
                console.log('docName: ' + this.docName);
                this.data = { ...data };
                if (!this.data.shareData) this.data.shareData = {};
                if (!this.data.shareData.shares) this.data.shareData.shares = [];
                this.loadUsersList = [];
                if (data && data.shareData) {
                    this.shareData = data.shareData;
                    this.isShared = data.shareData.isShared;
                    this.shareType = (data.shareData.shareType === 'edit' ? 'edit' : 'view');
                    this.isMapMine = data.shareData.isMine;
                    this.canShare = data.shareData.canShare;
                    this.isMapPubliclyShared = data.shareData.isPubliclyShared;
                } else {
                    this.shareData = null;
                    this.isShared = false;
                    this.shareType = '';
                    this.isMapMine = true;
                    this.canShare = false;
                    this.isMapPubliclyShared = false;
                }
                if (this.canShare) {
                    // this.showFacebook = this.origShowFacebook;
                } else {
                    this.showClassroom = false;
                }
                for (let i = 0; i < this.data.shareData.shares.length; i++) {
                    const share = this.data.shareData.shares[i];
                    if (share.trustee === 'anyoneWithLink') {
                        this.publicShareType = (share.role === 'writer' ? 'edit' : 'view');
                    } else {
                        const user = new UserItem();
                        user.caller = this;
                        user.email = share.trustee;
                        user.name = share.fullName || unknownUsername;
                        user.picture = share.picture;
                        user.role = share.role;
                        user.ownerNotified = share.ownerNotified;
                        this.loadUsersList.push(user);
                    }
                }
                this.saveUsersList = [...this.loadUsersList];
                this.updateFilter();
                this.loaderService.stopLoading('generic');
            },
            error: (error: any) => {
                this.loaderService.stopLoading('generic');
                console.log('loadData error: ' + error);
            }
        });
    }

    shareClassroom() {
        if (this.docId) {
            let shareClassroomEvent = '';
            switch (this.docType) {
                case MODULE_TYPE.EDITOR:
                    shareClassroomEvent = STATS.EDITOR.SHARE_CLASSROOM;
                    break;
                case MODULE_TYPE.PDF:
                    shareClassroomEvent = STATS.PDF.SHARE_CLASSROOM;
                    break;
                case MODULE_TYPE.MAP:
                    shareClassroomEvent = STATS.MAP.SHARE_CLASSROOM;
                    break;
            }
            this.apiService.addStatRecords([new StatDto(shareClassroomEvent)]).subscribe();
            window.open(`${window.location.origin}/share-classroom/${this.docId}/${this.docType}/${this.googleId}`, '_blank');

            //MATOMO Maps share classroom
            try {
                console.log('MATOMO event Map share Classoroom');
                this.tracker.trackEvent('Map', 'Share Classoroom', 'ShareClassoroom');
            } catch (e: any) {
                console.error('matomo error ' + e.message);
            }
        }
    }

    closeShareMap() {
        this.dialogRef.close();
    }

    copyToClipboard() {
        if (navigator.clipboard) {
            navigator.clipboard.writeText(this.urlToShare);
        } else {
            const input = document.createElement('input');
            document.body.appendChild(input);
            input.value = this.urlToShare;
            input.select();
            document.execCommand('copy');
            window.getSelection()?.removeAllRanges();
            document.body.removeChild(input);
        }
    }

    generateLink() {
        return new Promise((resolve, reject) => {
            if (!this.docId) return;
            let author = '';
            this.apiService.findOwnerByDocId(this.docId + '').subscribe((ownerData: any) => {
                if (ownerData && ownerData.owner.fullName) {
                    author = ownerData.owner.fullName;
                }
                this.shareLinkService.writeStaticDoc(this.docId ? this.docId : '', this.docType + '', this.docName + '', author).then((result: any) => {
                    this.urlToShare = result.url;
                    this.isLinkGenerated = true;
                    return resolve(null);
                }).catch((err) => {
                    console.log('ERROR: ' + err);
                    this.closeShareMap();
                    return reject();
                });
            });
        });
    }

    copyLink(showDone: boolean) {
        if (this.isLinkGenerated) {
            this.loaderService.startLoading('generic', 'PLEASE_WAIT');
            this.isSharing = true;
            this.copyToClipboard();
            this.loaderService.stopLoading('generic');
            if (showDone) {
                let message = this.translateService.instant(extract('LINK_COPIED'));
                if (!this.isShared && this.shareData && this.shareData.shares.length <= 1) {
                    message += '\n\n' + this.translateService.instant(extract('SHAREDOC_WARNING_NOT_SHARED'));
                }
                this.confirmationService.confirm(
                    this.translateService.instant(extract('MAIN_MENU_FILE-SHARE')),
                    message,
                    this.translateService.instant(extract('BUTTON_OK')),
                    '');

                //MATOMO Maps share link
                try {
                    console.log('MATOMO event Map share Link');
                    this.tracker.trackEvent('Map', 'Share Link', 'ShareLink');
                } catch (e: any) {
                    console.error('matomo error ' + e.message);
                }
            }
        }
    }

    showQRCodeAndCopyLink() {
        if (this.isLinkGenerated && this.docId) {
            // this.isSharing = true;
            this.copyLink(false);
            const link = this.shareLinkService.getLinkToShare(this.docId);
            // this.isSharing = false;
            let message = '';
            if (!this.showOnlyQrCode && !this.isShared) {
                message += this.translateService.instant(extract('SHAREDOC_WARNING_NOT_SHARED'));
            }
            this.showQRCode('DOC', link, 500, message);
            // this.isMapPubliclyShared = true;
        }
    }

    public showQRCode(type: string, link: string, size?: number, message?: string) {
        this.copyLink(false);

        const dialogQRCode = this.dialog.open(DialogQRCodeComponent);
        this.qrCodesService.createQRCode(link, size).then((dataUrl) => {
            dialogQRCode.componentInstance.link = link;
            if (this.docType) {
                dialogQRCode.componentInstance.setDocTypeIcon(this.shellStateService.getTypeIcon(this.docType));
            }
            dialogQRCode.componentInstance.setTitle(type);
            dialogQRCode.componentInstance.setQRCode(dataUrl);
            dialogQRCode.componentInstance.setMessage(message + '');

            //MATOMO Maps share link
            try {
                console.log('MATOMO event Map share Link');
                this.tracker.trackEvent('Map', 'Share Link', 'ShareLink');
            } catch (e: any) {
                console.error('matomo error ' + e.message);
            }
        });
    }

    warnUser() {
        const userMail = this.authenticationService.getUserEmail();
        this.messageBoxService.googleShareNotFirstUserWarning(userMail);
    }

    userCantShare() {
        this.messageBoxService.showUserCantShare();
    }

    private isValidEmail(email: string): boolean {
        const regex = /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/;
        return regex.test(email);
    }

    onAddUserKeyUp($event: any) {
        this.addUserEmail = $event.target.value.trim();
        this.addUserIsValid = this.isValidEmail(this.addUserEmail);
        if ($event.keyCode === 13) {
            this.addUser();
        } else {
            this.updateFilter();
        }
    }

    onAddUserChange($event: any) {
        this.addUserEmail = $event.target.value.trim();
        this.addUserIsValid = this.isValidEmail(this.addUserEmail);
        this.updateFilter();
    }

    optionSelected(value: string) {
        // console.log('+++++value: ' + value);
    }

    updateFilter() {
        this.shareEmails = new Array<string>();
        this.shareEmailsFreq = new Array<string>();
        this.shareEmailsGroup = new Array<string>();

        let isFreq: boolean = false;
        let isGroup: boolean = false;

        if (this.data && this.data.shareData && this.data.shareData.shareEmails) {
            for (let i = 0; i < this.data.shareData.shareEmails.length; i++) {
                const email = this.data.shareData.shareEmails[i]
                if (email.startsWith('%FREQ')) {
                    isFreq = true;
                    isGroup = false;
                }
                else if (email.startsWith('%GROUP')) {
                    isFreq = false;
                    isGroup = true;
                }
                else if (email.includes(this.addUserEmail)) {
                    if (isFreq) {
                        this.shareEmailsFreq.push(email);
                    }
                    else if (isGroup) {
                        this.shareEmailsGroup.push(email);
                    }
                }
            }
        }
    }

    async addUser() {
        this.addUserEmail = this.addUserNameField?.nativeElement.value.trim();
        this.addUserIsValid = this.isValidEmail(this.addUserEmail);
        if (this.addUserIsValid) {
            const user = new UserItem();
            user.caller = this;
            user.email = this.addUserEmail;
            user.name = '';
            user.picture = '';
            this.loaderService.startLoading('generic', 'PLEASE_WAIT');
            user.internalRole = this.shareType;
            user.ownerNotified = null;
            this.loaderService.stopLoading('generic');
            if (!this.isUserInList(user)) {
                this.saveUsersList.push(user);
                this.saveData();
                this.addUserEmail = '';
                if (this.addUserNameField) {
                    this.addUserNameField.nativeElement.value = '';
                }
                this.writeSharedStat();
            } else {
                console.log('+++USER ALREADY PRESENT');
            }
        } else {
            console.log('+++USER IS NOT A VALID MAIL')
        }
    }

    isUserInList(user: UserItem): boolean {
        let found = false;
        for (let i = 0; i < this.loadUsersList.length; i++) {
            if (this.loadUsersList[i].email === user.email) {
                found = true;
                break;
            }
        }
        return found;
    }

    deleteUser(user: UserItem) {
        for (let i = 0; i < this.saveUsersList.length; i++) {
            if (this.saveUsersList[i].email === user.email) {
                this.saveUsersList.splice(i, 1);
                this.askSaveData();
                break;
            }
        }
    }

    askSaveData() {
        if (this.saveDataId) { clearTimeout(this.saveDataId); }
        this.saveDataId = setTimeout(() => {
            this.saveData();
        }, 100);
    }

    saveData() {
        this.loaderService.startLoading('generic', 'PLEASE_WAIT');
        console.log('*****DATA SAVE*****');
        const shares = [];
        for (let i = 0; i < this.saveUsersList.length; i++) {
            const user = this.saveUsersList[i];
            shares.push({
                trustee: user.email,
                role: user.role,
                internalRole: user.ownerNotified
            });
        }
        if (this.publicShareType !== this.publicShareTypesOptions[0].value) {
            shares.push({
                trustee: 'anyoneWithLink',
                role: (this.publicShareType === 'edit' ? 'writer' : 'reader'),
                ownerNotified: null
            });
        }
        this.writeShareSubscription?.unsubscribe();
        this.writeShareSubscription = this.apiService.writeShares(this.docId + '', shares).subscribe({
            next: (res) => {
                this.loaderService.stopLoading('generic');
                this.loadData();
                this.showSnackBar();
            },
            error: (error) => {
                this.loaderService.stopLoading('generic');
            }
        });
    }

    public getDocIconStyle(docType: string) {
        switch (docType) {
            case MODULE_TYPE.MAP:
                return this.authenticationService.isMapEnabled() ? docType : 'document-not-active';
            case MODULE_TYPE.EDITOR:
                return this.authenticationService.isEditorEnabled() ? docType : 'document-not-active';
            case MODULE_TYPE.PDF:
                return this.authenticationService.isPdfEnabled() ? docType : 'document-not-active';
            default:
                return '';
        }
    }

    showSnackBar() {
        this._snackBar.open(
            this.translateService.instant('SHARE_SAVE_MESSAGE'),
            this.translateService.instant('BUTTON_OK').toUpperCase(),
            {
                duration: 4000,
                horizontalPosition: 'center',
                verticalPosition: 'bottom'
            });
    }

    private writeSharedStat() {
        let shareInternalEvent = '';
        switch (this.docType) {
            case MODULE_TYPE.EDITOR:
                shareInternalEvent = STATS.EDITOR.SHARE_INTERNAL;
                break;
            case MODULE_TYPE.PDF:
                shareInternalEvent = STATS.PDF.SHARE_INTERNAL;
                break;
            case MODULE_TYPE.MAP:
                shareInternalEvent = STATS.MAP.SHARE_INTERNAL;
                break;
        }
        this.apiService.addStatRecords([new StatDto(shareInternalEvent)]).subscribe();
    }

}


